import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import authService from './authService';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if we're currently refreshing to avoid multiple simultaneous refresh calls
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // If response contains user data, trigger a refresh event
    // This helps keep Redux store in sync with backend data
    if (response.data && (response.data.user || response.data.data?.user)) {
      const userData = response.data.user || response.data.data?.user;
      if (userData && userData.permissions) {
        // Dispatch event to update user data in Redux
        window.dispatchEvent(new CustomEvent('auth:userDataUpdate', {
          detail: { user: userData }
        }));
      }
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 - Unauthorized (token expired/invalid)
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Handle 403 - Forbidden (insufficient permissions)
    // If it's a permission error, try refreshing user data once
    if (error.response?.status === 403 && originalRequest && !originalRequest._retry) {
      const errorMessage = (error.response?.data as any)?.message || '';
      
      // Only auto-refresh for permission errors, not other 403 errors
      if (errorMessage.toLowerCase().includes('permission') || 
          errorMessage.toLowerCase().includes('insufficient')) {
        
        // If we're already refreshing, queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return api(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          console.log('🔄 Auto-refreshing user permissions due to 403 error...');
          // Refresh user data and token
          const refreshResponse = await authService.refresh();
          const newToken = refreshResponse.accessToken;
          
          // Update localStorage
          localStorage.setItem('token', newToken);
          const userData = {
            ...refreshResponse.user,
            companyId: refreshResponse.user.company?._id || refreshResponse.user.company,
            permissions: refreshResponse.user.permissions || {},
          };
          localStorage.setItem('user', JSON.stringify(userData));
          
          console.log('✅ Refresh successful. New permissions:', userData.permissions);
          
          // Dispatch custom event to update Redux store
          window.dispatchEvent(new CustomEvent('auth:refresh', { 
            detail: { user: userData, token: newToken } 
          }));
          
          // Update request header with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          
          // Process queued requests
          processQueue(null, newToken);
          isRefreshing = false;
          
          // Retry the original request
          console.log('🔄 Retrying original request after refresh...');
          return api(originalRequest);
        } catch (refreshError) {
          console.error('❌ Refresh failed:', refreshError);
          // Refresh failed, redirect to login
          processQueue(refreshError, null);
          isRefreshing = false;
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;
